import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const CHART_HEIGHT = 400;
const CHART_PADDING = { top: 18, right: 20, bottom: 44, left: 58 };

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function formatMetricValue(value, metric) {
  if (metric === 'cost') {
    return `$${(Number(value || 0) || 0).toFixed(6)}`;
  }

  return (Number(value || 0) || 0).toLocaleString();
}

function getBucketValue(bucket, key) {
  if (key === 'total') {
    return Number(bucket?.total || 0) || 0;
  }

  return Number(bucket?.series?.[key] || 0) || 0;
}

function advanceBucket(bucketStart, granularity) {
  const next = new Date(bucketStart);

  if (granularity === 'minute') {
    next.setUTCMinutes(next.getUTCMinutes() + 1);
    return next;
  }

  if (granularity === 'hour') {
    next.setUTCHours(next.getUTCHours() + 1);
    return next;
  }

  if (granularity === 'day') {
    next.setUTCDate(next.getUTCDate() + 1);
    return next;
  }

  if (granularity === 'week') {
    next.setUTCDate(next.getUTCDate() + 7);
    return next;
  }

  return new Date(Date.UTC(next.getUTCFullYear(), next.getUTCMonth() + 1, 1));
}

function useChartWidth() {
  const containerRef = useRef(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) {
      return undefined;
    }

    const updateWidth = () => {
      const nextWidth = node.getBoundingClientRect().width;
      setWidth(nextWidth > 0 ? nextWidth : 0);
    };

    updateWidth();

    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(() => updateWidth());
      observer.observe(node);
      return () => observer.disconnect();
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    return undefined;
  }, []);

  return { containerRef, width: Math.max(width, 320) };
}

export function InferenceStackedBarChart({
  chartData = [],
  seriesKeys = [],
  metric = 'records',
  metricLabel = 'Record Count',
  getSeriesColor,
  isLoading = false,
  xDomainStartMs = null,
  xDomainEndExclusiveMs = null,
  axisStartLabel = '',
  axisEndLabel = '',
  granularity = 'day',
}) {
  const { containerRef, width } = useChartWidth();
  const [hoverIndex, setHoverIndex] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0 });

  const activeSeriesKeys = useMemo(
    () => (seriesKeys && seriesKeys.length > 0 ? seriesKeys : ['total']),
    [seriesKeys]
  );

  const geometry = useMemo(() => {
    const safeData = Array.isArray(chartData)
      ? [...chartData].sort(
          (a, b) => (a?.bucketStart?.getTime?.() || 0) - (b?.bucketStart?.getTime?.() || 0)
        )
      : [];

    const innerWidth = Math.max(1, width - CHART_PADDING.left - CHART_PADDING.right);
    const innerHeight = Math.max(1, CHART_HEIGHT - CHART_PADDING.top - CHART_PADDING.bottom);

    const fallbackStart =
      safeData[0]?.bucketStart instanceof Date ? safeData[0].bucketStart.getTime() : 0;
    const fallbackEnd =
      safeData[safeData.length - 1]?.bucketStart instanceof Date
        ? advanceBucket(safeData[safeData.length - 1].bucketStart, granularity).getTime()
        : fallbackStart + 1;

    const domainStart = Number.isFinite(xDomainStartMs) ? xDomainStartMs : fallbackStart;
    const domainEndExclusive = Number.isFinite(xDomainEndExclusiveMs)
      ? xDomainEndExclusiveMs
      : fallbackEnd;
    const domainDuration = Math.max(1, domainEndExclusive - domainStart);

    const xForMs = (ms) =>
      CHART_PADDING.left + ((ms - domainStart) / domainDuration) * innerWidth;

    let maxValue = 0;
    for (const bucket of safeData) {
      maxValue = Math.max(maxValue, Number(bucket?.total || 0) || 0);
    }

    const yMax = maxValue > 0 ? maxValue : 1;
    const yForValue = (value) =>
      CHART_PADDING.top + innerHeight - (Math.max(0, value) / yMax) * innerHeight;

    const bars = safeData.map((bucket) => {
      const startMs = bucket.bucketStart.getTime();
      const endMs = Math.min(advanceBucket(bucket.bucketStart, granularity).getTime(), domainEndExclusive);
      const startX = xForMs(startMs);
      const endX = xForMs(endMs);
      const fullWidth = Math.max(6, endX - startX);
      const barWidth = Math.max(4, fullWidth - 2);
      const x = startX + Math.max(0, (fullWidth - barWidth) / 2);

      let runningValue = Number(bucket?.total || 0) || 0;
      const segments = activeSeriesKeys.map((key) => {
        const value = getBucketValue(bucket, key);
        const topValue = runningValue;
        runningValue -= value;

        return {
          key,
          x,
          y: yForValue(topValue),
          height: Math.max(0, yForValue(runningValue) - yForValue(topValue)),
          width: barWidth,
          color: getSeriesColor(key),
          value,
        };
      });

      return {
        bucket,
        x,
        centerX: x + barWidth / 2,
        width: barWidth,
        y: yForValue(bucket.total || 0),
        height: Math.max(0, yForValue(0) - yForValue(bucket.total || 0)),
        segments,
      };
    });

    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((ratio) => {
      const value = yMax * ratio;
      return {
        key: ratio,
        label: formatMetricValue(value, metric),
        y: yForValue(value),
      };
    });

    return {
      bars,
      yTicks,
    };
  }, [activeSeriesKeys, chartData, getSeriesColor, granularity, metric, width, xDomainEndExclusiveMs, xDomainStartMs]);

  const hoveredBar = hoverIndex != null ? geometry.bars[hoverIndex] : null;

  const tooltipSeriesKeys = useMemo(() => {
    if (!hoveredBar?.bucket) {
      return [];
    }
    return activeSeriesKeys
      .map((key) => ({
        key,
        value: Number(getBucketValue(hoveredBar.bucket, key)) || 0,
      }))
      .filter(({ value }) => value > 0)
      .sort((a, b) => b.value - a.value)
      .map(({ key }) => key);
  }, [activeSeriesKeys, hoveredBar]);

  const handleMouseMove = (event) => {
    if (!containerRef.current || geometry.bars.length === 0 || typeof window === 'undefined') {
      return;
    }

    const rect = containerRef.current.getBoundingClientRect();
    const localX = clamp(event.clientX - rect.left, CHART_PADDING.left, width - CHART_PADDING.right);

    let nearestIndex = 0;
    let nearestDistance = Math.abs(geometry.bars[0].centerX - localX);
    for (let index = 1; index < geometry.bars.length; index += 1) {
      const distance = Math.abs(geometry.bars[index].centerX - localX);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestIndex = index;
      }
    }

    setHoverIndex(nearestIndex);
    setTooltipPosition({
      left: clamp(rect.left + geometry.bars[nearestIndex].centerX + 14, 12, window.innerWidth - 280),
      top: rect.top + 18,
    });
  };

  if (isLoading) {
    return <div className="admin-inferences-chart-empty">Loading chart...</div>;
  }

  if (!Array.isArray(chartData) || chartData.length === 0) {
    return <div className="admin-inferences-chart-empty">No inference data for the current result set.</div>;
  }

  return (
    <>
      <div
        ref={containerRef}
        className="admin-inferences-chart-wrapper"
        onMouseLeave={() => setHoverIndex(null)}
      >
        <svg
          width={width}
          height={CHART_HEIGHT}
          className="admin-inferences-chart-svg"
          onMouseMove={handleMouseMove}
        >
          {geometry.yTicks.map((tick) => (
            <g key={tick.key}>
              <line
                x1={CHART_PADDING.left}
                y1={tick.y}
                x2={width - CHART_PADDING.right}
                y2={tick.y}
                stroke="rgba(255,255,255,0.08)"
                strokeWidth="1"
              />
              <text
                x={CHART_PADDING.left - 10}
                y={tick.y + 4}
                textAnchor="end"
                fill="#9ca3af"
                fontSize="11"
              >
                {tick.label}
              </text>
            </g>
          ))}

          <line
            x1={CHART_PADDING.left}
            y1={CHART_HEIGHT - CHART_PADDING.bottom}
            x2={width - CHART_PADDING.right}
            y2={CHART_HEIGHT - CHART_PADDING.bottom}
            stroke="rgba(255,255,255,0.14)"
            strokeWidth="1"
          />

          {geometry.bars.map((bar, index) => (
            <g key={bar.bucket.key || `${bar.bucket.label}-${index}`}>
              {bar.segments.map((segment) => (
                <rect
                  key={segment.key}
                  x={segment.x}
                  y={segment.y}
                  width={segment.width}
                  height={segment.height}
                  rx="3"
                  fill={segment.color}
                  opacity="0.96"
                />
              ))}

              {hoveredBar?.bucket?.key === bar.bucket.key && (
                <rect
                  className="admin-inferences-chart-bar-outline"
                  x={bar.x}
                  y={bar.y}
                  width={bar.width}
                  height={bar.height}
                  rx="3"
                />
              )}
            </g>
          ))}

          {hoveredBar && (
            <line
              x1={hoveredBar.centerX}
              y1={CHART_PADDING.top}
              x2={hoveredBar.centerX}
              y2={CHART_HEIGHT - CHART_PADDING.bottom}
              stroke="rgba(255,255,255,0.35)"
              strokeDasharray="4 4"
              strokeWidth="1"
              pointerEvents="none"
            />
          )}

          <text
            x={CHART_PADDING.left}
            y={CHART_HEIGHT - 12}
            textAnchor="start"
            fill="#9ca3af"
            fontSize="11"
          >
            {axisStartLabel}
          </text>
          <text
            x={width - CHART_PADDING.right}
            y={CHART_HEIGHT - 12}
            textAnchor="end"
            fill="#9ca3af"
            fontSize="11"
          >
            {axisEndLabel}
          </text>
        </svg>

        {activeSeriesKeys.length > 1 && (
          <div className="admin-inferences-chart-legend">
            {activeSeriesKeys.map((key) => (
              <div className="admin-inferences-chart-legend-item" key={key}>
                <span
                  className="admin-inferences-chart-legend-dot"
                  style={{ backgroundColor: getSeriesColor(key) }}
                />
                <span>{key === 'total' ? metricLabel : key}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {hoveredBar &&
        ReactDOM.createPortal(
          <div
            className="stripe-chart-tooltip admin-inferences-chart-hover-tooltip"
            style={{
              position: 'fixed',
              left: tooltipPosition.left,
              top: tooltipPosition.top,
            }}
          >
            <div className="stripe-chart-tooltip-title">{hoveredBar.bucket.label}</div>
            <div className="admin-inferences-chart-tooltip-list">
              {tooltipSeriesKeys.map((key) => (
                <div className="admin-inferences-chart-tooltip-row" key={key}>
                  <span
                    className="admin-inferences-chart-tooltip-dot"
                    style={{ backgroundColor: getSeriesColor(key) }}
                  />
                  <span className="admin-inferences-chart-tooltip-name">
                    {key === 'total' ? metricLabel : key}
                  </span>
                  <span className="admin-inferences-chart-tooltip-value">
                    {formatMetricValue(getBucketValue(hoveredBar.bucket, key), metric)}
                  </span>
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
